import { useCallback } from 'react';
import { GqlDispensaries } from 'types/graphql';
import useDispensaryFlag from 'shared/hooks/use-dispensary-flag';
import { fonts } from 'shared/constants/menu-fonts';
import { navigationBarColors, navigationBarColorsV2, linkColors, linkColorsV2 } from 'shared/constants/menu-colors';

type Color = {
  background: string;
  border: string;
  color: string;
  key: string;
};

type FontFamily = {
  fontFamily: string;
  fontFamilyEncoded?: string;
  fallbacks: Array<string>;
};

type UseWebCustomizationSettingsCallbackReturn = {
  linkColor?: Color;
  navBarColor?: Color;
  fontFamily: FontFamily | null;
};

type UseWebCustomizationSettingsReturn = () => UseWebCustomizationSettingsCallbackReturn;

export function useGetWebCustomizationSettings(
  dispensary: GqlDispensaries | undefined
): UseWebCustomizationSettingsReturn {
  const canUseWebCustomizationSettings = useDispensaryFlag(
    `rollout.customize-admin-primary-navigation`,
    dispensary?.id
  );

  return useCallback(() => {
    const webCustomizationSettings = dispensary?.webCustomizationSettings;
    const legacyColorSettings = dispensary?.colorSettings ?? {};

    if (canUseWebCustomizationSettings && webCustomizationSettings) {
      const { linkColor, navBarColor } = webCustomizationSettings.colorSettings;
      const { family } = webCustomizationSettings.fontSettings;

      return {
        linkColor: linkColorsV2[linkColor],
        navBarColor: navigationBarColorsV2[navBarColor],
        fontFamily: fonts[family],
      };
    }

    const { linkColor, navBarColor } = legacyColorSettings;

    return {
      linkColor: linkColors.find(({ key }) => key === linkColor),
      navBarColor: navigationBarColors.find(({ key }) => key === navBarColor),
      fontFamily: null,
    };
  }, [canUseWebCustomizationSettings, dispensary?.colorSettings, dispensary?.webCustomizationSettings]);
}
